import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../../shared/services/api.service';
import { AuthService } from '../../../shared/services/auth.service';

export interface Country {
  _id: string;
  displayName: string;
  country_code: string;
  dialingCode: string;
  currency: any;
  status: string;
  icon: string;
  isRecipient: boolean;
  isOrigination: boolean;
}

export interface PackageData {
  id: string;
  type: string;
  price: number;
  amount: number;
  day: number;
  is_unlimited: boolean;
  title: string;
  short_info: string | null;
  qr_installation: string;
  manual_installation: string;
  is_fair_usage_policy: boolean;
  fair_usage_policy: string | null;
  data: string;
  voice: string | null;
  text: string | null;
  net_price: number;
  prices: {
    net_price: Record<string, number>;
    recommended_retail_price: Record<string, number>;
  };
}

export interface Operator {
  id: number;
  style: string;
  gradient_start: string;
  gradient_end: string;
  type: string;
  is_prepaid: boolean;
  title: string;
  esim_type: string;
  warning: string | null;
  apn_type: string;
  apn_value: string;
  is_roaming: boolean;
  info: string[];
  image: {
    width: number;
    height: number;
    url: string;
  };
  plan_type: string;
  activation_policy: string;
  is_kyc_verify: boolean;
  rechargeability: boolean;
  other_info: string;
  coverages: {
    name: string;
    code: string;
    networks: {
      name: string;
      types: string[];
    }[];
  }[];
  install_window_days: number;
  topup_grace_window_days: number;
  apn: {
    ios: {
      apn_type: string;
      apn_value: string;
    };
    android: {
      apn_type: string;
      apn_value: string;
    };
  };
  packages: PackageData[];
  countries: {
    country_code: string;
    title: string;
    image: {
      width: number;
      height: number;
      url: string;
    };
  }[];
}

export interface PackageCountryData {
  slug: string;
  country_code: string;
  title: string;
  image: {
    width: number;
    height: number;
    url: string;
  };
  operators: Operator[];
}

export interface PackagesResponse {
  error: boolean;
  data: {
    pricing: {
      model: string;
      discount_percentage: number;
    };
    data: PackageCountryData[];
  };
}

@Component({
  standalone: false,
  selector: 'app-packages',
  templateUrl: './packages.component.html',
  styleUrls: ['./package.component.scss']
})
export class PackagesComponent implements OnInit {
  packages: PackageCountryData[] = [];
  countries: Country[] = [];
  selectedCountryCode: string = '';
  loading = false;
  error: string | null = null;
  selectedOperator: Operator | null = null;
  displayedPackages: PackageData[] = [];

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadCountries();
  }

  loadCountries(): void {
    this.apiService.getData('/admin/get-data?type=country&_page=1&_limit=150').subscribe({
      next: (response: any) => {
        if (!response.error && response.data) {
          this.countries = response.data.sort((a: Country, b: Country) => {
            const nameA = a.displayName.toLowerCase();
            const nameB = b.displayName.toLowerCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });
        } else {
          this.error = 'Failed to load countries data';
        }
      },
      error: (err) => {
        console.error('Error fetching countries:', err);
        this.error = 'Error loading countries';
      }
    });
  }

  loadPackages(): void {
    if (!this.selectedCountryCode) {
      this.error = 'Please select a country first';
      return;
    }

    this.loading = true;
    this.error = null;
    this.selectedOperator = null; // Reset selected operator when loading new packages
    
    // The JWT interceptor will automatically add the token header
    const apiUrl = `/api/airalo/packages?filter[type]=local&filter[country]=${this.selectedCountryCode}`;
    this.apiService.getData(apiUrl).subscribe({
      next: (response: PackagesResponse) => {
        if (!response.error && response.data && response.data.data) {
          this.packages = response.data.data;
        } else {
          this.error = 'Failed to load packages data';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching packages:', err);
        this.error = 'Error loading packages';
        this.loading = false;
      }
    });
  }

  onCountryChange(event: any): void {
    this.selectedCountryCode = event.target.value;
    // Clear old data when country changes
    this.packages = [];
    this.selectedOperator = null;
    this.displayedPackages = [];
    this.error = null;
  }

  submitCountry(): void {
    // Clear old data before loading new packages
    this.packages = [];
    this.selectedOperator = null;
    this.displayedPackages = [];
    this.error = null;
    this.loadPackages();
  }

  showOperatorPackages(operator: Operator): void {
    this.selectedOperator = operator;
    this.displayedPackages = operator.packages;
  }

  goBackToOperators(): void {
    this.selectedOperator = null;
    this.displayedPackages = [];
  }

  trackByPackage(index: number, pkg: PackageData): string {
    return pkg.id;
  }

  trackByOperator(index: number, operator: Operator): number {
    return operator.id;
  }

  trackByPackageCountry(index: number, pkgCountry: PackageCountryData): string {
    return pkgCountry.slug;
  }
}